// AlphaPhaseCountdown.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { styled } from "@mui/material";

export interface AlphaPhaseCountdownProps {
    target: string | number | Date;
    onComplete?: () => void;
    className?: string;
    style?: React.CSSProperties;
    labels?: { d?: string; h?: string; m?: string; s?: string };
}

type Parts = { d: number; h: number; m: number; s: number };

function toTs(t: string | number | Date): number | null {
    if (t instanceof Date) return t.getTime();
    if (typeof t === "number") return t;
    const ms = new Date(t).getTime();
    return Number.isNaN(ms) ? null : ms;
}
const clamp = (ms: number) => (ms < 0 ? 0 : ms);
function split(ms: number): Parts {
    const total = Math.floor(ms / 1000);
    return {
        d: Math.floor(total / 86400),
        h: Math.floor((total % 86400) / 3600),
        m: Math.floor((total % 3600) / 60),
        s: total % 60,
    };
}
const pad2 = (n: number) => n.toString().padStart(2, "0");

/* ===== Styled components ===== */
const Root = styled("section")({
    minHeight: "100dvh",
    width: "100%",
    display: "grid",
    placeItems: "center",
    padding: 16,
    boxSizing: "border-box",
});

const Glass = styled("div")({
    width: "fit-content",
    maxWidth: "92vw",
    background:
        "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(255,255,255,0.06))",
    border: "1px solid rgba(255,255,255,0.18)",
    borderRadius: 16,
    padding: 16,
    backdropFilter: "blur(10px)",
    WebkitBackdropFilter: "blur(10px)",
    boxShadow:
        "0 10px 35px rgba(0,0,0,0.45), inset 0 0 0 1px rgba(255,255,255,0.06)",
    "@media (max-width: 480px)": {
        padding: 12,
        background:
            "linear-gradient(180deg, rgba(255,255,255,0.10), rgba(0,0,0,0.4))",
    },
});

const Title = styled("div")({
    fontSize: 14,
    fontWeight: 800,
    letterSpacing: 0.4,
    color: "#EAF1F9",
    textAlign: "center",
    marginBottom: 10,
    textTransform: "uppercase",
    opacity: 0.9,
    "@media (max-width: 480px)": {
        fontSize: 9,
        marginBottom: 8,
    },
});

const Grid = styled("div")({
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(56px, 1fr))",
    gap: 8,
    "@media (max-width: 480px)": {
        gap: 6,
        gridTemplateColumns: "repeat(4, minmax(48px, 1fr))",
    },
});

const Cell = styled("div")({
    display: "grid",
    gridTemplateRows: "auto auto",
    justifyItems: "center",
    alignItems: "center",
    padding: "8px 6px",
    borderRadius: 10,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.10)",
    "@media (max-width: 480px)": {
        padding: "6px 4px",
    },
});

const Value = styled("div")({
    fontWeight: 800,
    fontSize: 22,
    color: "#f5f8fc",
    lineHeight: 1,
    "@media (max-width: 480px)": {
        fontSize: 12,
    },
});

const Label = styled("div")({
    marginTop: 4,
    fontWeight: 600,
    fontSize: 11,
    color: "rgba(233,238,245,0.78)",
    textTransform: "uppercase",
    letterSpacing: 1,
    "@media (max-width: 480px)": {
        fontSize: 8,
    },
});

const Footer = styled("p")({
    fontSize: 12,
    fontWeight: 400,
    letterSpacing: 0.4,
    color: "silver",
    textAlign: "center",
    marginBottom: 0,
    marginTop: 10,
    opacity: 0.9,
    "@media (max-width: 480px)": {
        fontSize: 10,
        marginTop: 4,
    },
});

/* ===== Component ===== */
export default function AlphaPhaseCountdown({
    target,
    onComplete,
    className,
    style,
    labels,
}: AlphaPhaseCountdownProps) {
    const targetMs = useMemo(() => toTs(target), [target]);
    const [parts, setParts] = useState<Parts>(() => split(0));
    const firedRef = useRef(false);
    
    useEffect(() => {
        if (targetMs == null) {
            setParts(split(0));
            return;
        }
        const tick = () => {
            const remain = clamp(targetMs - Date.now());
            setParts(split(remain));
            if (remain === 0 && !firedRef.current) {
                firedRef.current = true;
                onComplete?.();
            }
        };
        tick();
        const id = window.setInterval(tick, 1000);
        return () => window.clearInterval(id);
    }, [targetMs, onComplete]);
    
    const L = {
        d: labels?.d ?? "Days",
        h: labels?.h ?? "Hours",
        m: labels?.m ?? "Minutes",
        s: labels?.s ?? "Seconds",
    };
    
    return (
        <Root className={className} style={style}>
            <Glass>
                <Title>Alpha testing in live markets (invite-only) — Phase 2 starts in</Title>
                <Grid>
                    <Cell>
                        <Value>{parts.d}</Value>
                        <Label>{L.d}</Label>
                    </Cell>
                    <Cell>
                        <Value>{pad2(parts.h)}</Value>
                        <Label>{L.h}</Label>
                    </Cell>
                    <Cell>
                        <Value>{pad2(parts.m)}</Value>
                        <Label>{L.m}</Label>
                    </Cell>
                    <Cell>
                        <Value>{pad2(parts.s)}</Value>
                        <Label>{L.s}</Label>
                    </Cell>
                </Grid>
                <Footer>Real users will apply the technology to their trades.</Footer>
            </Glass>
        </Root>
    );
}
